import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { client_service } from '@/services/client_service';
import type {
  Client,
  ClientCreateData,
  ClientUpdateData,
  ClientFilters,
  ClientStatistics,
  ClientType,
} from '@/types/client_types';
import type { RootState } from './index';

/**
 * Client state interface
 */
interface ClientState {
  // List management
  clients: Client[];
  total_count: number;
  current_page: number;
  page_size: number;
  has_next: boolean;
  has_previous: boolean;

  // Single client
  selected_client: Client | null;
  
  // Filters
  filters: ClientFilters;
  search_query: string;

  // Statistics
  statistics: ClientStatistics | null;

  // Loading states
  is_loading: boolean;
  is_creating: boolean;
  is_updating: boolean;
  is_deleting: boolean;
  is_loading_stats: boolean;

  // Errors
  error: string | null;
  create_error: string | null;
  update_error: string | null;
  delete_error: string | null;

  // Cache
  cache: Record<number, Client>;
  cache_timestamp: number;
}

/**
 * Initial state
 */
const initial_state: ClientState = {
  clients: [],
  total_count: 0,
  current_page: 1,
  page_size: 20,
  has_next: false,
  has_previous: false,
  selected_client: null,
  filters: {},
  search_query: '',
  statistics: null,
  is_loading: false,
  is_creating: false,
  is_updating: false,
  is_deleting: false,
  is_loading_stats: false,
  error: null,
  create_error: null,
  update_error: null,
  delete_error: null,
  cache: {},
  cache_timestamp: 0,
};

// ============ ASYNC THUNKS ============

/**
 * Fetch paginated clients with filters
 */
export const fetch_clients = createAsyncThunk(
  'client/fetch_clients',
  async (
    params: { page?: number; page_size?: number; filters?: ClientFilters },
    { rejectWithValue }
  ) => {
    try {
      const { page = 1, page_size = 20, filters = {} } = params;
      const response = await client_service.get_clients(page, page_size, filters);
      return { response, page, page_size, filters };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch clients');
    }
  }
);

/**
 * Fetch all clients (for dropdowns)
 */
export const fetch_all_clients = createAsyncThunk(
  'client/fetch_all_clients',
  async (params: { client_type?: ClientType }, { rejectWithValue }) => {
    try {
      const clients = await client_service.get_all_clients((params as any).client_type);
      return clients;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch clients');
    }
  }
);

/**
 * Fetch single client by ID
 */
export const fetch_client_by_id = createAsyncThunk(
  'client/fetch_client_by_id',
  async (id: number, { rejectWithValue }) => {
    try {
      const client = await client_service.get_client(id);
      return client;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch client');
    }
  }
);

/**
 * Create new client
 */
export const create_client = createAsyncThunk(
  'client/create_client',
  async (data: ClientCreateData, { rejectWithValue }) => {
    try {
      const client = await client_service.create_client(data);
      return client;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create client');
    }
  }
);

/**
 * Update existing client
 */
export const update_client = createAsyncThunk(
  'client/update_client',
  async ({ id, data }: { id: number; data: ClientUpdateData }, { rejectWithValue }) => {
    try {
      const client = await client_service.update_client(id, data);
      return client;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update client');
    }
  }
);

/**
 * Delete client
 */
export const delete_client = createAsyncThunk(
  'client/delete_client',
  async (id: number, { rejectWithValue }) => {
    try {
      await client_service.delete_client(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete client');
    }
  }
);

/**
 * Search clients
 */
export const search_clients = createAsyncThunk(
  'client/search_clients',
  async (query: string, { rejectWithValue }) => {
    try {
      const clients = await client_service.search_clients(query);
      return clients;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Search failed');
    }
  }
);

/**
 * Fetch client statistics
 */
export const fetch_client_stats = createAsyncThunk(
  'client/fetch_client_stats',
  async (_, { rejectWithValue }) => {
    try {
      const stats = await client_service.get_client_stats();
      return stats;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch statistics');
    }
  }
);

/**
 * Bulk update client status
 */
export const bulk_update_client_status = createAsyncThunk(
  'client/bulk_update_status',
  async (
    { client_ids, is_active }: { client_ids: number[]; is_active: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await client_service.bulk_update_status(client_ids, is_active);
      return { client_ids, is_active, response };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Bulk update failed');
    }
  }
);

// ============ SLICE ============

const client_slice = createSlice({
  name: 'client',
  initialState: initial_state,
  reducers: {
    /**
     * Set filters
     */
    set_filters: (state, action: PayloadAction<ClientFilters>) => {
      state.filters = action.payload;
      state.current_page = 1; // Reset to first page when filters change
    },

    /**
     * Clear filters
     */
    clear_filters: (state) => {
      state.filters = {};
      state.search_query = '';
      state.current_page = 1;
    },

    /**
     * Set search query
     */
    set_search_query: (state, action: PayloadAction<string>) => {
      state.search_query = action.payload;
      state.current_page = 1;
    },

    /**
     * Set selected client
     */
    set_selected_client: (state, action: PayloadAction<Client | null>) => {
      state.selected_client = action.payload;
    },

    /**
     * Clear selected client
     */
    clear_selected_client: (state) => {
      state.selected_client = null;
    },

    /**
     * Set page size
     */
    set_page_size: (state, action: PayloadAction<number>) => {
      state.page_size = action.payload;
      state.current_page = 1;
    },

    /**
     * Clear errors
     */
    clear_client_errors: (state) => {
      state.error = null;
      state.create_error = null;
      state.update_error = null;
      state.delete_error = null;
    },

    /**
     * Update client in cache
     */
    update_client_in_cache: (state, action: PayloadAction<Client>) => {
      state.cache[action.payload.id] = action.payload;
      state.cache_timestamp = Date.now();
    },

    /**
     * Clear cache
     */
    clear_client_cache: (state) => {
      state.cache = {};
      state.cache_timestamp = 0;
    },
  },
  extraReducers: (builder) => {
    // ===== FETCH CLIENTS =====
    builder
      .addCase(fetch_clients.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(fetch_clients.fulfilled, (state, action) => {
        state.is_loading = false;
        state.clients = action.payload.response.results;
        state.total_count = action.payload.response.count;
        state.current_page = action.payload.page;
        state.page_size = action.payload.page_size;
        state.has_next = !!action.payload.response.next;
        state.has_previous = !!action.payload.response.previous;
        state.filters = action.payload.filters;

        // Update cache
        action.payload.response.results.forEach((client) => {
          state.cache[client.id] = client;
        });
        state.cache_timestamp = Date.now();
      })
      .addCase(fetch_clients.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
      });

    // ===== FETCH ALL CLIENTS =====
    builder
      .addCase(fetch_all_clients.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(fetch_all_clients.fulfilled, (state, action) => {
        state.is_loading = false;
        // Update cache only
        action.payload.forEach((client) => {
          state.cache[client.id] = client;
        });
        state.cache_timestamp = Date.now();
      })
      .addCase(fetch_all_clients.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
      });

    // ===== FETCH CLIENT BY ID =====
    builder
      .addCase(fetch_client_by_id.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(fetch_client_by_id.fulfilled, (state, action) => {
        state.is_loading = false;
        state.selected_client = action.payload;
        state.cache[action.payload.id] = action.payload;
        state.cache_timestamp = Date.now();
      })
      .addCase(fetch_client_by_id.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
      });

    // ===== CREATE CLIENT =====
    builder
      .addCase(create_client.pending, (state) => {
        state.is_creating = true;
        state.create_error = null;
      })
      .addCase(create_client.fulfilled, (state, action) => {
        state.is_creating = false;
        state.clients.unshift(action.payload); // Add to beginning of list
        state.total_count += 1;
        state.cache[action.payload.id] = action.payload;
        state.cache_timestamp = Date.now();
      })
      .addCase(create_client.rejected, (state, action) => {
        state.is_creating = false;
        state.create_error = action.payload as string;
      });

    // ===== UPDATE CLIENT =====
    builder
      .addCase(update_client.pending, (state) => {
        state.is_updating = true;
        state.update_error = null;
      })
      .addCase(update_client.fulfilled, (state, action) => {
        state.is_updating = false;
        
        // Update in clients list
        const index = state.clients.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }

        // Update selected client if it's the same
        if (state.selected_client?.id === action.payload.id) {
          state.selected_client = action.payload;
        }

        // Update cache
        state.cache[action.payload.id] = action.payload;
        state.cache_timestamp = Date.now();
      })
      .addCase(update_client.rejected, (state, action) => {
        state.is_updating = false;
        state.update_error = action.payload as string;
      });

    // ===== DELETE CLIENT =====
    builder
      .addCase(delete_client.pending, (state) => {
        state.is_deleting = true;
        state.delete_error = null;
      })
      .addCase(delete_client.fulfilled, (state, action) => {
        state.is_deleting = false;
        
        // Remove from clients list
        state.clients = state.clients.filter((c) => c.id !== action.payload);
        state.total_count -= 1;

        // Clear selected if it's the deleted client
        if (state.selected_client?.id === action.payload) {
          state.selected_client = null;
        }

        // Remove from cache
        delete state.cache[action.payload];
      })
      .addCase(delete_client.rejected, (state, action) => {
        state.is_deleting = false;
        state.delete_error = action.payload as string;
      });

    // ===== SEARCH CLIENTS =====
    builder
      .addCase(search_clients.pending, (state) => {
        state.is_loading = true;
        state.error = null;
      })
      .addCase(search_clients.fulfilled, (state, action) => {
        state.is_loading = false;
        // Cast search results to Client[] - they should have compatible structure
        state.clients = action.payload as Client[];
        state.total_count = action.payload.length;
      })
      .addCase(search_clients.rejected, (state, action) => {
        state.is_loading = false;
        state.error = action.payload as string;
      });

    // ===== FETCH STATISTICS =====
    builder
      .addCase(fetch_client_stats.pending, (state) => {
        state.is_loading_stats = true;
        state.error = null;
      })
      .addCase(fetch_client_stats.fulfilled, (state, action) => {
        state.is_loading_stats = false;
        state.statistics = action.payload;
      })
      .addCase(fetch_client_stats.rejected, (state, action) => {
        state.is_loading_stats = false;
        state.error = action.payload as string;
      });

    // ===== BULK UPDATE STATUS =====
    builder
      .addCase(bulk_update_client_status.pending, (state) => {
        state.is_updating = true;
        state.update_error = null;
      })
      .addCase(bulk_update_client_status.fulfilled, (state, action) => {
        state.is_updating = false;
        
        // Update clients in list
        action.payload.client_ids.forEach((id) => {
          const client = state.clients.find((c) => c.id === id);
          if (client) {
            client.is_active = action.payload.is_active;
          }

          // Update cache
          if (state.cache[id]) {
            state.cache[id].is_active = action.payload.is_active;
          }
        });

        state.cache_timestamp = Date.now();
      })
      .addCase(bulk_update_client_status.rejected, (state, action) => {
        state.is_updating = false;
        state.update_error = action.payload as string;
      });
  },
});

// ============ ACTIONS ============

export const {
  set_filters,
  clear_filters,
  set_search_query,
  set_selected_client,
  clear_selected_client,
  set_page_size,
  clear_client_errors,
  update_client_in_cache,
  clear_client_cache,
} = client_slice.actions;

// ============ SELECTORS ============

export const select_clients = (state: RootState) => state.client.clients;
export const select_total_clients = (state: RootState) => state.client.total_count;
export const select_current_page = (state: RootState) => state.client.current_page;
export const select_page_size = (state: RootState) => state.client.page_size;
export const select_has_next = (state: RootState) => state.client.has_next;
export const select_has_previous = (state: RootState) => state.client.has_previous;
export const select_selected_client = (state: RootState) => state.client.selected_client;
export const select_client_filters = (state: RootState) => state.client.filters;
export const select_search_query = (state: RootState) => state.client.search_query;
export const select_client_statistics = (state: RootState) => state.client.statistics;
export const select_client_loading = (state: RootState) => state.client.is_loading;
export const select_client_creating = (state: RootState) => state.client.is_creating;
export const select_client_updating = (state: RootState) => state.client.is_updating;
export const select_client_deleting = (state: RootState) => state.client.is_deleting;
export const select_client_error = (state: RootState) => state.client.error;

/**
 * Get client from cache by ID
 */
export const select_client_by_id = (id: number) => (state: RootState) => {
  return state.client.cache[id] || null;
};

/**
 * Get landlords only
 */
export const select_landlords = (state: RootState) => {
  return state.client.clients.filter((c) => c.client_type === 'LANDLORD');
};

/**
 * Get tenants only
 */
export const select_tenants = (state: RootState) => {
  return state.client.clients.filter((c) => c.client_type === 'TENANT');
};

/**
 * Get active clients only
 */
export const select_active_clients = (state: RootState) => {
  return state.client.clients.filter((c) => c.is_active);
};

/**
 * Get clients with outstanding balance
 */
export const select_clients_with_outstanding = (state: RootState) => {
  return state.client.clients.filter((c) => c.outstanding_amount > 0);
};

// ============ EXPORT REDUCER ============

export default client_slice.reducer;